import React, { useEffect, useMemo, useState, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { PawPrint, User, Camera, Check, ChevronRight, ChevronLeft, MapPin, Clock, Heart, Droplets, Award, ArrowLeft, Ticket, Plus, Trash2, Search, Sparkles, Gift, ShieldCheck, Smartphone, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { supabase } from '../utils/supabase';
import { redirectToWompi } from '../utils/wompi';
import { isValidEmail, isValidColombianPhone, isValidCedula, isValidFullName, isValidPetName, isValidPetBreed, firstError } from '../utils/validators';
import { formatCOP, priceBreakdown } from '../utils/format';
import { DiscountCodeInput } from '../components/DiscountCodeInput';
import { DiscountCode, calculateDiscount } from '../utils/discounts';
import { PetFormCard, PetEntry, newPet, validatePhotoFile } from '../components/PetFormCard';
type RegistrationStep = 1 | 1.5 | 2 | 3 | 4;
type TicketType = 'pet_lover' | 'deportista' | 'extra_pet';
interface HumanEntry {
  id: string;
  name: string;
  email: string;
  phone: string;
  idNumber: string;
  photoFile?: File | null;
  photoUrlFromMobile?: string | null;
  photoSessionId?: string;
  showQR?: boolean;
}
const newHuman = (): HumanEntry => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  email: '',
  phone: '',
  idNumber: '',
  photoFile: null,
  photoUrlFromMobile: null,
  photoSessionId: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  showQR: false
});
interface KidEntry {
  id: string;
  name: string;
  birthdate: string; // ISO date YYYY-MM-DD
  photoFile?: File | null;
  photoUrlFromMobile?: string | null;
  photoSessionId?: string;
  showQR?: boolean;
}
const newKid = (): KidEntry => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  birthdate: '',
  photoFile: null,
  photoUrlFromMobile: null,
  photoSessionId: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  showQR: false
});
// Event date for the under-12 validation
const EVENT_DATE = new Date('2026-07-26');
function isUnder12AtEvent(isoBirthdate: string): boolean {
  if (!isoBirthdate) return false;
  const birth = new Date(isoBirthdate);
  if (isNaN(birth.getTime())) return false;
  const ageMs = EVENT_DATE.getTime() - birth.getTime();
  const ageYears = ageMs / (1000 * 60 * 60 * 24 * 365.25);
  return ageYears < 12 && ageYears >= 0;
}
interface LocalPetEntry extends PetEntry {
  responsables: string[];
}
const createLocalPet = (): LocalPetEntry => ({
  ...newPet(),
  responsables: []
});
export function Caminata5KPage() {
  const location = useLocation();
  // Deep-link support:
  //  - /caminata-canina#inscripcion       → scrolls to the form (step 1: choose pack)
  //  - /caminata-canina?pack=pet-lover    → opens the Pack Pet Lover form directly
  //  - /caminata-canina?pack=deportista   → opens the Pack Deportista form directly
  //  - /caminata-canina?pack=extra-pet    → opens the Pack Mascota Extra flow directly
  //    (the pack name is also accepted in the hash, e.g. #pet-lover)
  const linkTarget = `${location.search} ${location.hash}`;
  const requestedPack: TicketType | null = /pet[-_]?lover/i.test(linkTarget) ? 'pet_lover' : /deportista/i.test(linkTarget) ? 'deportista' : /extra[-_]?pet|mascota[-_]?extra/i.test(linkTarget) ? 'extra_pet' : null;
  useEffect(() => {
    if (location.hash === '#inscripcion' || location.search || location.hash) {
      const timeout = setTimeout(() => {
        document.getElementById('inscripcion')?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [location.hash, location.search]);
  const [step, setStep] = useState<RegistrationStep>(requestedPack === 'extra_pet' ? 1.5 : requestedPack ? 2 : 1);
  const [ticketType, setTicketType] = useState<TicketType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedReglamento, setAcceptedReglamento] = useState(false);
  const [acceptedPetTerms, setAcceptedPetTerms] = useState(false);
  const [acceptedPetHabeas, setAcceptedPetHabeas] = useState(false);
  const [humans, setHumans] = useState<HumanEntry[]>([newHuman()]);
  const [kids, setKids] = useState<KidEntry[]>([]);
  const [pets, setPets] = useState<LocalPetEntry[]>([createLocalPet()]);
  // Combined limit: max 8 items total (adults + kids + pets)
  const MAX_TOTAL_ITEMS = 8;
  const totalItems = humans.length + kids.length + pets.length;
  const [extraValidation, setExtraValidation] = useState({
    email: '',
    isValidating: false,
    validatedRegistration: null as any
  });
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCode | null>(null);
  // If the link asks for a specific pack, preselect it so the form opens ready.
  useEffect(() => {
    if (!requestedPack) return;
    setTicketType(requestedPack);
    if (requestedPack === 'pet_lover') {
      setHumans([newHuman()]);
      setKids([]);
      setPets([createLocalPet()]);
      setStep(2);
    } else if (requestedPack === 'deportista') {
      setHumans([newHuman()]);
      setKids([]);
      setPets([]);
      setStep(2);
    } else if (requestedPack === 'extra_pet') {
      setPets([createLocalPet()]);
      setStep(1.5);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedPack]);
  // Computed price
  const price = useMemo(() => {
    if (ticketType === 'extra_pet') {
      return pets.length * 30000;
    }
    return humans.length * 70000 + kids.length * 40000 + pets.length * 30000;
  }, [ticketType, humans.length, kids.length, pets.length]);
  const discountAmount = appliedDiscount ? calculateDiscount(price, appliedDiscount) : 0;
  const subtotalAfterDiscount = price - discountAmount;
  const calculatedIva = subtotalAfterDiscount * 0.19;
  const finalTotal = subtotalAfterDiscount + calculatedIva;
  // Poll mobile uploads for each pet that has QR open
  useEffect(() => {
    const activePets = pets.filter(p => p.showQR && !p.photoUrlFromMobile);
    if (activePets.length === 0) return;
    const interval = setInterval(async () => {
      for (const pet of activePets) {
        const {
          data
        } = await supabase.storage.from('expositor-documents').list(`mobile/${pet.photoSessionId}`);
        if (data && data.length > 0) {
          const file = data.find(f => f.name.startsWith('pet_photo_'));
          if (file) {
            const {
              data: {
                publicUrl
              }
            } = supabase.storage.from('expositor-documents').getPublicUrl(`mobile/${pet.photoSessionId}/${file.name}`);
            setPets(prev => prev.map(p => p.id === pet.id ? {
              ...p,
              photoUrlFromMobile: publicUrl,
              showQR: false
            } : p));
            toast.success('Foto subida desde otro dispositivo ✓');
          }
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [pets]);
  // Poll mobile uploads for each human that has QR open
  useEffect(() => {
    const activeHumans = humans.filter(h => h.showQR && !h.photoUrlFromMobile);
    if (activeHumans.length === 0) return;
    const interval = setInterval(async () => {
      for (const human of activeHumans) {
        const {
          data
        } = await supabase.storage.from('expositor-documents').list(`mobile/${human.photoSessionId}`);
        if (data && data.length > 0) {
          const file = data.find(f => f.name.startsWith('human_photo_'));
          if (file) {
            const {
              data: {
                publicUrl
              }
            } = supabase.storage.from('expositor-documents').getPublicUrl(`mobile/${human.photoSessionId}/${file.name}`);
            setHumans(prev => prev.map(h => h.id === human.id ? {
              ...h,
              photoUrlFromMobile: publicUrl,
              showQR: false
            } : h));
            toast.success('Foto de participante subida desde otro dispositivo ✓');
          }
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [humans]);
  const addHuman = () => {
    if (totalItems >= MAX_TOTAL_ITEMS) {
      toast.error(`Máximo ${MAX_TOTAL_ITEMS} ítems en total (adultos + niños + mascotas)`);
      return;
    }
    setHumans([...humans, newHuman()]);
  };
  const removeHuman = (id: string) => {
    // Titular (humans[0]) cannot be removed
    if (humans.length <= 1 || humans[0].id === id) return;
    setHumans(humans.filter(h => h.id !== id));
    // Also remove this human from any pet's responsables
    setPets(prev => prev.map(p => ({
      ...p,
      responsables: p.responsables.filter(rId => rId !== id)
    })));
  };
  const updateHuman = <K extends keyof HumanEntry,>(id: string, field: K, value: HumanEntry[K]) => {
    setHumans(prev => prev.map(h => h.id === id ? {
      ...h,
      [field]: value
    } : h));
  };
  const addKid = () => {
    if (totalItems >= MAX_TOTAL_ITEMS) {
      toast.error(`Máximo ${MAX_TOTAL_ITEMS} ítems en total (adultos + niños + mascotas)`);
      return;
    }
    setKids([...kids, newKid()]);
  };
  const removeKid = (id: string) => {
    setKids(kids.filter(k => k.id !== id));
  };
  const updateKid = <K extends keyof KidEntry,>(id: string, field: K, value: KidEntry[K]) => {
    setKids(prev => prev.map(k => k.id === id ? {
      ...k,
      [field]: value
    } : k));
  };
  const uploadKidPhoto = async (file: File): Promise<string | null> => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const fileName = `kid_photo_${timestamp}_${randomStr}.${ext}`;
    const {
      error
    } = await supabase.storage.from('expositor-documents').upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg'
    });
    if (error) {
      console.error('Photo upload error:', error);
      toast.error(`No se pudo subir la foto del niño: ${error.message}`);
      return null;
    }
    const {
      data: {
        publicUrl
      }
    } = supabase.storage.from('expositor-documents').getPublicUrl(fileName);
    return publicUrl;
  };
  const addPet = () => {
    if (totalItems >= MAX_TOTAL_ITEMS && ticketType !== 'extra_pet') {
      toast.error(`Máximo ${MAX_TOTAL_ITEMS} ítems en total (adultos + niños + mascotas)`);
      return;
    }
    if (ticketType === 'extra_pet' && pets.length >= 5) {
      toast.error('Máximo 5 mascotas por registro');
      return;
    }
    setPets([...pets, createLocalPet()]);
  };
  const removePet = (id: string) => {
    if (pets.length <= 1 && ticketType !== 'deportista') return;
    setPets(pets.filter(p => p.id !== id));
  };
  const updatePet = (id: string, fn: (p: LocalPetEntry) => LocalPetEntry) => {
    setPets(prev => prev.map(p => p.id === id ? fn(p) : p));
  };
  const handlePetPhotoChange = (petId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!validatePhotoFile(file)) return;
    updatePet(petId, p => ({
      ...p,
      photoFile: file,
      photoUrlFromMobile: null
    }));
    toast.success('✓ Foto seleccionada');
  };
  const uploadPetPhoto = async (file: File): Promise<string | null> => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const fileName = `pet_photo_${timestamp}_${randomStr}.${ext}`;
    const {
      error
    } = await supabase.storage.from('expositor-documents').upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg'
    });
    if (error) {
      console.error('Photo upload error:', error);
      toast.error(`No se pudo subir la foto: ${error.message}`);
      return null;
    }
    const {
      data: {
        publicUrl
      }
    } = supabase.storage.from('expositor-documents').getPublicUrl(fileName);
    return publicUrl;
  };
  const uploadHumanPhoto = async (file: File): Promise<string | null> => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const fileName = `human_photo_${timestamp}_${randomStr}.${ext}`;
    const {
      error
    } = await supabase.storage.from('expositor-documents').upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || 'image/jpeg'
    });
    if (error) {
      console.error('Photo upload error:', error);
      toast.error(`No se pudo subir la foto: ${error.message}`);
      return null;
    }
    const {
      data: {
        publicUrl
      }
    } = supabase.storage.from('expositor-documents').getPublicUrl(fileName);
    return publicUrl;
  };
  const validateExtraPet = async () => {
    const emailCheck = isValidEmail(extraValidation.email);
    if (!emailCheck.ok) {
      toast.error(emailCheck.message);
      return;
    }
    setExtraValidation(prev => ({
      ...prev,
      isValidating: true
    }));
    try {
      const emailNormalized = extraValidation.email.trim().toLowerCase();
      const {
        data,
        error
      } = await supabase.from('registrations_5k').select('id, full_name, email, phone, document_id').ilike('email', emailNormalized).eq('status', 'paid').order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      if (error) {
        console.error('Supabase validation error:', error);
        toast.error('Error consultando inscripción. Intenta de nuevo.');
        setExtraValidation(prev => ({
          ...prev,
          isValidating: false
        }));
        return;
      }
      if (!data) {
        toast.error('No encontramos una inscripción pagada con este email. Asegúrate de haber comprado tu pack primero.');
        setExtraValidation(prev => ({
          ...prev,
          isValidating: false
        }));
        return;
      }
      setExtraValidation(prev => ({
        ...prev,
        isValidating: false,
        validatedRegistration: data
      }));
      setHumans([{
        id: 'validated-human',
        name: data.full_name,
        email: data.email,
        phone: data.phone || '',
        idNumber: data.document_id || ''
      }]);
      setStep(3);
      toast.success(`¡Hola ${data.full_name.split(' ')[0]}! Puedes agregar tus mascotas extra.`);
    } catch (err) {
      console.error(err);
      toast.error('Error validando inscripción');
      setExtraValidation(prev => ({
        ...prev,
        isValidating: false
      }));
    }
  };
  const handleRegistration = async () => {
    // Human data validation
    if (ticketType !== 'extra_pet') {
      for (let i = 0; i < humans.length; i++) {
        const h = humans[i];
        const humanCheck = firstError(isValidFullName(h.name), isValidEmail(h.email), isValidColombianPhone(h.phone), isValidCedula(h.idNumber));
        if (!humanCheck.ok) {
          toast.error(`Participante ${i + 1}: ${humanCheck.message}`);
          return;
        }
      }
      // Kid validation (photo NOT required)
      for (let i = 0; i < kids.length; i++) {
        const k = kids[i];
        if (!isValidFullName(k.name).ok) {
          toast.error(`Niño ${i + 1}: el nombre es obligatorio`);
          return;
        }
        if (!k.birthdate) {
          toast.error(`Niño ${i + 1}: la fecha de nacimiento es obligatoria`);
          return;
        }
        if (!isUnder12AtEvent(k.birthdate)) {
          toast.error(`Niño ${i + 1}: debe ser menor de 12 años al 26 de julio de 2026`);
          return;
        }
      }
    }
    // Pet data validation
    if (ticketType === 'pet_lover' || ticketType === 'extra_pet') {
      for (let i = 0; i < pets.length; i++) {
        const p = pets[i];
        if (!p.photoFile && !p.photoUrlFromMobile) {
          toast.error(`Mascota ${i + 1}: la foto es obligatoria`);
          return;
        }
        const petCheck = firstError(isValidPetName(p.name), isValidPetBreed(p.breed));
        if (!petCheck.ok) {
          toast.error(`Mascota ${i + 1}: ${petCheck.message}`);
          return;
        }
        if (!p.size) {
          toast.error(`Mascota ${i + 1}: selecciona el tamaño`);
          return;
        }
        if (p.bio.length > 80) {
          toast.error(`Mascota ${i + 1}: la bio debe tener máximo 80 caracteres`);
          return;
        }
        if (ticketType === 'pet_lover' && p.responsables.length === 0) {
          toast.error(`Mascota ${i + 1}: selecciona al menos un responsable`);
          return;
        }
      }
    }
    const needsReglamento = ticketType !== 'deportista';
    if (!acceptedTerms || needsReglamento && !acceptedReglamento) {
      toast.error(needsReglamento ? 'Debes aceptar los Términos y el Reglamento del evento' : 'Debes aceptar los Términos y Condiciones');
      return;
    }
    setIsSubmitting(true);
    const amountInCents = Math.round(finalTotal * 100);
    try {
      // Upload human photos first
      const humansWithUrls: {
        human: HumanEntry;
        photoUrl: string | null;
      }[] = [];
      if (ticketType !== 'extra_pet') {
        for (const human of humans) {
          let photoUrl = human.photoUrlFromMobile || null;
          if (human.photoFile) {
            photoUrl = await uploadHumanPhoto(human.photoFile);
          }
          humansWithUrls.push({
            human,
            photoUrl
          });
        }
      }
      // Upload kid photos (optional)
      const kidsWithUrls: {
        kid: KidEntry;
        photoUrl: string | null;
      }[] = [];
      if (ticketType !== 'extra_pet') {
        for (const kid of kids) {
          let photoUrl = kid.photoUrlFromMobile || null;
          if (kid.photoFile) {
            photoUrl = await uploadKidPhoto(kid.photoFile);
          }
          kidsWithUrls.push({
            kid,
            photoUrl
          });
        }
      }
      // Upload pet photos (if any pets)
      const petsWithUrls: {
        pet: LocalPetEntry;
        photoUrl: string | null;
      }[] = [];
      if (ticketType === 'pet_lover' || ticketType === 'extra_pet') {
        for (const pet of pets) {
          let photoUrl = pet.photoUrlFromMobile;
          if (pet.photoFile) {
            photoUrl = await uploadPetPhoto(pet.photoFile);
          }
          if (!photoUrl) {
            toast.error(`No se pudo subir la foto de ${pet.name}`);
            setIsSubmitting(false);
            return;
          }
          petsWithUrls.push({
            pet,
            photoUrl
          });
        }
      }
      // Insert parent row
      const primaryHuman = humans[0];
      const {
        data: regData,
        error: regError
      } = await supabase.from('registrations_5k').insert({
        full_name: primaryHuman.name,
        email: primaryHuman.email,
        phone: primaryHuman.phone,
        document_id: primaryHuman.idNumber,
        ticket_type: ticketType,
        linked_registration_id: ticketType === 'extra_pet' ? extraValidation.validatedRegistration.id : null,
        total_amount: amountInCents,
        status: 'pending_payment',
        payment_provider: 'wompi',
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        photo_url: humansWithUrls[0]?.photoUrl || null
      }).select('id').single();
      if (regError || !regData) throw regError;
      const registrationId = regData.id;
      if (ticketType !== 'extra_pet') {
        // Insert adult attendees
        const adultAttendees = humansWithUrls.map(({
          human: h,
          photoUrl
        }, i) => ({
          registration_id: registrationId,
          attendee_index: i,
          is_primary: i === 0,
          is_minor: false,
          full_name: h.name,
          email: h.email,
          phone: h.phone,
          document_id: h.idNumber,
          amount_cents: 7000000,
          photo_url: photoUrl
        }));
        // Insert kid attendees (no email/phone/document; uses birthdate)
        const kidAttendees = kidsWithUrls.map(({
          kid: k,
          photoUrl
        }, i) => ({
          registration_id: registrationId,
          attendee_index: humansWithUrls.length + i,
          is_primary: false,
          is_minor: true,
          full_name: k.name,
          birthdate: k.birthdate,
          amount_cents: 4000000,
          photo_url: photoUrl
        }));
        const attendeesToInsert = [...adultAttendees, ...kidAttendees];
        const {
          data: attendeesData,
          error: attendeesError
        } = await supabase.from('registration_attendees').insert(attendeesToInsert).select('id, attendee_index');
        if (attendeesError) throw attendeesError;
        // Insert pets and responsables (kids cannot be responsables — only adults)
        if (ticketType === 'pet_lover' && pets.length > 0) {
          const petsToInsert = petsWithUrls.map(({
            pet,
            photoUrl
          }, index) => ({
            registration_id: registrationId,
            name: pet.name,
            breed: pet.breed,
            age: pet.age,
            size: pet.size,
            is_primary: index === 0,
            amount_cents: 3000000,
            photo_url: photoUrl,
            bio: pet.bio || null
          }));
          const {
            data: petsData,
            error: petsError
          } = await supabase.from('registration_pets').insert(petsToInsert).select('id');
          if (petsError) throw petsError;
          // Insert junction records (only adult humans can be responsables)
          const junctionToInsert = [];
          for (let i = 0; i < pets.length; i++) {
            const pet = pets[i];
            const petDbId = petsData[i].id;
            for (const humanId of pet.responsables) {
              const humanIndex = humans.findIndex(h => h.id === humanId);
              if (humanIndex !== -1) {
                const attendeeDbId = attendeesData.find(a => a.attendee_index === humanIndex)?.id;
                if (attendeeDbId) {
                  junctionToInsert.push({
                    pet_id: petDbId,
                    attendee_id: attendeeDbId
                  });
                }
              }
            }
          }
          if (junctionToInsert.length > 0) {
            const {
              error: junctionError
            } = await supabase.from('registration_pet_responsables').insert(junctionToInsert);
            if (junctionError) throw junctionError;
          }
        }
      } else {
        // Extra pet flow
        const petsToInsert = petsWithUrls.map(({
          pet,
          photoUrl
        }) => ({
          registration_id: registrationId,
          name: pet.name,
          breed: pet.breed,
          age: pet.age,
          size: pet.size,
          is_primary: false,
          amount_cents: 3000000,
          photo_url: photoUrl,
          bio: pet.bio || null
        }));
        const {
          error: petsError
        } = await supabase.from('registration_pets').insert(petsToInsert);
        if (petsError) throw petsError;
      }
      toast.success('¡Inscripción registrada! Redirigiendo al pago…');
      await redirectToWompi({
        reference: registrationId,
        amountInCents,
        customer: {
          email: primaryHuman.email,
          fullName: primaryHuman.name,
          phone: primaryHuman.phone,
          legalIdType: 'CC',
          legalId: primaryHuman.idNumber || undefined
        },
        redirectUrl: `${window.location.origin}/gracias?id=${registrationId}`
      });
    } catch (err) {
      console.error('Error registrando inscripción:', err);
      toast.error('No pudimos procesar tu inscripción. Intenta de nuevo o contáctanos.');
      setIsSubmitting(false);
    }
  };
  // Helper to determine active visual step (1 to 4)
  const visualStep = step === 1.5 ? 1 : step;
  return <div className="pt-20 bg-brand-navy" data-id="element-1404">
      <Toaster position="top-center" richColors data-id="element-1405" />

      {/* Hero Banner */}
      <section className="relative py-24 bg-brand-navy overflow-hidden" data-id="element-1406">
        <div className="absolute inset-0" data-id="element-1407">
          <img src="https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?w=1920&q=80&auto=format&fit=crop" alt="" className="w-full h-full object-cover opacity-30" data-id="element-1408" />
          <div className="absolute inset-0 bg-brand-navy/70" data-id="element-1409"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1410">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-cyan hover:text-white transition-colors mb-8 text-sm font-medium" data-id="element-1411">
            <ArrowLeft className="w-4 h-4" data-id="element-1412" /> Volver al inicio
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl text-white mb-4" data-id="element-1413">
            Caminata Canina <br data-id="element-1414" />
            <span className="text-brand-cyan" data-id="element-1415">Pet Lovers</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/80 max-w-2xl mx-auto mb-8" data-id="element-1416">
            6,5 km de conexión, amor y deporte junto a tu mejor amigo de cuatro
            patas.
          </motion.p>
          <div className="flex flex-wrap justify-center gap-6 text-white/90" data-id="element-1417">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full" data-id="element-1418">
              <Clock className="w-5 h-5 text-brand-cyan" data-id="element-1419" /> 7:00 AM
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full" data-id="element-1420">
              <MapPin className="w-5 h-5 text-brand-green" data-id="element-1421" /> Parque del
              Bienestar COMFAMA
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full" data-id="element-1422">
              <PawPrint className="w-5 h-5 text-brand-yellow" data-id="element-1423" /> Todas las razas
            </div>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section id="inscripcion" className="py-16 bg-gradient-to-b from-white to-gray-50 scroll-mt-24" data-id="element-1424">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1425">
          <h2 className="text-4xl font-bold text-brand-navy text-center mb-4" data-id="element-1426">
            {['Inscríbete', 'Ahora'].map((word, i) => <motion.span key={i} initial={{
            opacity: 0,
            y: 30,
            filter: 'blur(10px)'
          }} whileInView={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-block shimmer-text mr-3" data-id="element-1427">
                {word}
              </motion.span>)}
          </h2>
          <p className="text-gray-600 text-center mb-12" data-id="element-1428">
            Completa tu registro en simples pasos
          </p>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12" data-id="element-1429">
            {[1, 2, 3, 4].map(s => <Fragment key={s} data-id="element-1430">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${visualStep >= s ? 'bg-brand-cyan text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-1431">
                  {visualStep > s ? <Check className="w-5 h-5" data-id="element-1432" /> : s}
                </div>
                {s < 4 && <div className={`w-12 md:w-20 h-1 mx-1 rounded-full transition-all ${visualStep > s ? 'bg-brand-cyan' : 'bg-gray-200'}`} data-id="element-1433" />}
              </Fragment>)}
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-xl border border-gray-100" data-id="element-1434">
            {/* Step 1: Choose type */}
            {step === 1 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1435">
                <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-1436">
                  ¿Cómo vas a participar?
                </h3>
                <p className="text-gray-500 text-center mb-8" data-id="element-1437">
                  Elige tu modalidad de inscripción
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1438">
                  {/* Pack Pet Lover */}
                  <button onClick={() => {
                setTicketType('pet_lover');
                setHumans([newHuman()]);
                setKids([]);
                setPets([createLocalPet()]);
                setStep(2);
              }} className={`relative p-6 rounded-2xl border-2 transition-all text-left hover:shadow-lg flex flex-col h-full ${ticketType === 'pet_lover' ? 'border-brand-cyan bg-brand-cyan/5' : 'border-brand-cyan/30 hover:border-brand-cyan'}`} data-id="element-1439">
                    <div className="absolute top-0 right-0 bg-brand-cyan text-white px-3 py-1 rounded-bl-xl rounded-tr-xl font-bold text-[10px]" data-id="element-1440">
                      MÁS POPULAR
                    </div>
                    <div className="w-12 h-12 bg-brand-cyan/10 rounded-xl flex items-center justify-center text-brand-cyan mb-4" data-id="element-1441">
                      <Ticket className="w-6 h-6" data-id="element-1442" />
                    </div>
                    <h4 className="text-lg font-bold text-brand-navy mb-1" data-id="element-1443">
                      Pack Pet Lover
                    </h4>
                    <p className="text-gray-500 text-xs mb-4 flex-grow" data-id="element-1444">
                      1 persona + 1 mascota
                    </p>
                    <p className="text-2xl font-bold text-brand-navy" data-id="element-1445">
                      $100.000{' '}
                      <span className="text-xs font-normal text-gray-400" data-id="element-1446">
                        COP
                      </span>
                    </p>
                  </button>

                  {/* Pack Deportista */}
                  <button onClick={() => {
                setTicketType('deportista');
                setHumans([newHuman()]);
                setKids([]);
                setPets([]);
                setStep(2);
              }} className={`p-6 rounded-2xl border-2 transition-all text-left hover:shadow-lg flex flex-col h-full ${ticketType === 'deportista' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`} data-id="element-1447">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-500 mb-4" data-id="element-1448">
                      <Ticket className="w-6 h-6" data-id="element-1449" />
                    </div>
                    <h4 className="text-lg font-bold text-brand-navy mb-1" data-id="element-1450">
                      Pack Deportista
                    </h4>
                    <p className="text-gray-500 text-xs mb-4 flex-grow" data-id="element-1451">
                      1 persona sin mascota
                    </p>
                    <p className="text-2xl font-bold text-brand-navy" data-id="element-1452">
                      $70.000{' '}
                      <span className="text-xs font-normal text-gray-400" data-id="element-1453">
                        COP
                      </span>
                    </p>
                  </button>

                  {/* Pack Mascota Extra */}
                  <button onClick={() => {
                setTicketType('extra_pet');
                setPets([createLocalPet()]);
                setStep(1.5);
              }} className={`relative p-6 rounded-2xl border-2 transition-all text-left hover:shadow-lg flex flex-col h-full ${ticketType === 'extra_pet' ? 'border-brand-yellow bg-brand-yellow/5' : 'border-gray-200 hover:border-brand-yellow'}`} data-id="element-1454">
                    <div className="absolute top-0 right-0 bg-brand-yellow text-brand-navy px-3 py-1 rounded-bl-xl rounded-tr-xl font-bold text-[10px]" data-id="element-1455">
                      ADICIONAL
                    </div>
                    <div className="w-12 h-12 bg-brand-yellow/10 rounded-xl flex items-center justify-center text-brand-yellow mb-4" data-id="element-1456">
                      <Ticket className="w-6 h-6" data-id="element-1457" />
                    </div>
                    <h4 className="text-lg font-bold text-brand-navy mb-1" data-id="element-1458">
                      Mascota Extra
                    </h4>
                    <p className="text-gray-500 text-xs mb-4 flex-grow" data-id="element-1459">
                      Para 1 mascota extra
                    </p>
                    <p className="text-2xl font-bold text-brand-navy" data-id="element-1460">
                      $30.000{' '}
                      <span className="text-xs font-normal text-gray-400" data-id="element-1461">
                        COP
                      </span>
                    </p>
                  </button>
                </div>
              </motion.div>}

            {/* Step 1.5: Validate Extra Pet */}
            {step === 1.5 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1462">
                <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-1463">
                  Verifica tu inscripción
                </h3>
                <p className="text-gray-500 text-center mb-8" data-id="element-1464">
                  Para agregar una mascota extra, debes tener una inscripción
                  activa.
                </p>

                <div className="max-w-md mx-auto space-y-5" data-id="element-1465">
                  <div data-id="element-1466">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1467">
                      Email de registro *
                    </label>
                    <input type="email" value={extraValidation.email} onChange={e => setExtraValidation({
                  ...extraValidation,
                  email: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-yellow focus:border-transparent outline-none" placeholder="El email que usaste al comprar" data-id="element-1468" />
                  </div>

                  <div className="flex gap-4 mt-8" data-id="element-1469">
                    <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-1470">
                      <ChevronLeft className="w-5 h-5" data-id="element-1471" /> Volver
                    </button>
                    <button onClick={validateExtraPet} disabled={extraValidation.isValidating} className="flex-1 py-3 rounded-xl bg-brand-yellow text-brand-navy font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors shadow-lg disabled:opacity-70" data-id="element-1472">
                      {extraValidation.isValidating ? 'Buscando...' : 'Validar'}{' '}
                      <Search className="w-4 h-4" data-id="element-1473" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Step 2: Human Registration */}
            {step === 2 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1474">
                <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-1475">
                  Participantes
                </h3>
                <p className="text-gray-500 text-center mb-8" data-id="element-1476">
                  Ingresa los datos de las personas que asistirán
                </p>

                <div className="bg-brand-cyan/5 border border-brand-cyan/20 rounded-2xl p-4 flex items-start gap-3 mb-6" data-id="element-1477">
                  <Sparkles className="w-5 h-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-1478" />
                  <p className="text-sm text-gray-700" data-id="element-1479">
                    <strong className="text-brand-navy" data-id="element-1480">
                      Tu inscripción incluye 1 participante.
                    </strong>{' '}
                    Puedes agregar hasta 4 acompañantes adicionales por $70.000
                    c/u.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-1481">
                  <AnimatePresence data-id="element-1482">
                    {humans.map((human, index) => <motion.div key={human.id} initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm relative" data-id="element-1483">
                        <div className="flex justify-between items-center mb-4" data-id="element-1484">
                          <h4 className="font-bold text-brand-navy flex items-center gap-2" data-id="element-1485">
                            {index === 0 ? 'Participante Principal (Titular)' : `Acompañante #${index}`}
                            {index > 0 && <span className="bg-brand-cyan/20 text-brand-cyan text-xs px-2 py-0.5 rounded-full" data-id="element-1486">
                                +$70.000
                              </span>}
                          </h4>
                          {index > 0 && <button onClick={() => removeHuman(human.id)} className="text-red-500 hover:text-red-700 transition-colors p-1" data-id="element-1487">
                              <Trash2 className="w-4 h-4" data-id="element-1488" />
                            </button>}
                        </div>
                        <div className="mb-4 pb-4 border-b border-gray-100" data-id="element-1489">
                          <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1490">
                            Foto del participante *
                          </label>
                          <p className="text-[10px] text-gray-500 mb-3" data-id="element-1491">
                            Ideal que la compartas — nos ayuda a identificarte
                            el día del evento
                          </p>
                          <div className="flex flex-col sm:flex-row gap-4 items-start" data-id="element-1492">
                            <div className="w-32 flex-shrink-0" data-id="element-1493">
                              <input type="file" accept="image/*" onChange={e => {
                          const file = e.target.files?.[0] || null;
                          if (!file) return;
                          if (!validatePhotoFile(file)) return;
                          updateHuman(human.id, 'photoFile', file);
                          updateHuman(human.id, 'photoUrlFromMobile', null);
                          toast.success('✓ Foto seleccionada');
                        }} className="hidden" id={`human-photo-${human.id}`} data-id="element-1494" />
                              <label htmlFor={`human-photo-${human.id}`} className={`relative block w-full aspect-square rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-colors ${human.photoFile || human.photoUrlFromMobile ? 'border-brand-green' : 'border-gray-300 hover:border-brand-cyan'}`} data-id="element-1495">
                                {human.photoFile || human.photoUrlFromMobile ? <>
                                    <img src={human.photoFile ? URL.createObjectURL(human.photoFile) : human.photoUrlFromMobile!} alt="Foto participante" className="absolute inset-0 w-full h-full object-cover" data-id="element-1496" />
                                    <div className="absolute top-1 right-1 w-6 h-6 bg-brand-green rounded-full flex items-center justify-center shadow" data-id="element-1497">
                                      <Check className="w-4 h-4 text-white" data-id="element-1498" />
                                    </div>
                                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-2" data-id="element-1499">
                                    <Camera className="w-6 h-6 mb-1" data-id="element-1500" />
                                    <span className="text-[10px] text-center font-medium leading-tight" data-id="element-1501">
                                      Subir foto
                                    </span>
                                  </div>}
                              </label>
                              {!(human.photoFile || human.photoUrlFromMobile) && <button type="button" onClick={() => updateHuman(human.id, 'showQR', !human.showQR)} className="mt-2 text-brand-cyan hover:text-blue-700 text-[10px] font-semibold flex items-center justify-center gap-1 w-full" data-id="element-1502">
                                  <Smartphone className="w-3 h-3" data-id="element-1503" />
                                  Subir desde otro dispositivo
                                </button>}
                              {(human.photoFile || human.photoUrlFromMobile) && <button type="button" onClick={() => {
                          updateHuman(human.id, 'photoFile', null);
                          updateHuman(human.id, 'photoUrlFromMobile', null);
                        }} className="mt-2 text-red-500 hover:text-red-700 text-[10px] font-semibold flex items-center justify-center gap-1 w-full" data-id="element-1504">
                                  <Trash2 className="w-3 h-3" data-id="element-1505" />
                                  Quitar foto
                                </button>}
                            </div>

                            <AnimatePresence data-id="element-1506">
                              {human.showQR && !(human.photoFile || human.photoUrlFromMobile) && <motion.div initial={{
                          opacity: 0,
                          width: 0
                        }} animate={{
                          opacity: 1,
                          width: 'auto'
                        }} exit={{
                          opacity: 0,
                          width: 0
                        }} className="bg-gray-50 border border-gray-200 rounded-xl p-3 overflow-hidden flex-shrink-0" data-id="element-1507">
                                    <p className="text-[10px] text-gray-600 mb-2 text-center" data-id="element-1508">
                                      Escanea con otro dispositivo
                                    </p>
                                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`${window.location.origin}/subir-documento?session=${human.photoSessionId}&doc=human_photo`)}`} alt="QR Code" className="w-24 h-24 mx-auto rounded-lg shadow-sm" data-id="element-1509" />
                                    <div className="flex items-center justify-center gap-1 text-brand-cyan text-[10px] font-medium mt-2" data-id="element-1510">
                                      <Loader2 className="w-3 h-3 animate-spin" data-id="element-1511" />
                                      Esperando...
                                    </div>
                                  </motion.div>}
                            </AnimatePresence>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1512">
                          <div data-id="element-1513">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1514">
                              Nombre completo *
                            </label>
                            <input type="text" value={human.name} onChange={e => updateHuman(human.id, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan outline-none text-sm" placeholder="Tu nombre completo" data-id="element-1515" />
                          </div>
                          <div data-id="element-1516">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1517">
                              Cédula / Documento *
                            </label>
                            <input type="text" value={human.idNumber} onChange={e => updateHuman(human.id, 'idNumber', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan outline-none text-sm" placeholder="Número de documento" data-id="element-1518" />
                          </div>
                          <div data-id="element-1519">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1520">
                              Email *
                            </label>
                            <input type="email" value={human.email} onChange={e => updateHuman(human.id, 'email', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan outline-none text-sm" placeholder="tu@email.com" data-id="element-1521" />
                          </div>
                          <div data-id="element-1522">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1523">
                              WhatsApp *
                            </label>
                            <input type="tel" value={human.phone} onChange={e => updateHuman(human.id, 'phone', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-cyan outline-none text-sm" placeholder="+57 300 000 0000" data-id="element-1524" />
                          </div>
                        </div>
                      </motion.div>)}
                  </AnimatePresence>

                  {/* Kids cards */}
                  <AnimatePresence data-id="element-1525">
                    {kids.map((kid, idx) => {
                  const ageOk = kid.birthdate ? isUnder12AtEvent(kid.birthdate) : null;
                  return <motion.div key={kid.id} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="bg-amber-50/40 border border-amber-200 rounded-2xl p-5 shadow-sm relative" data-id="element-1526">
                          <div className="flex justify-between items-center mb-2" data-id="element-1527">
                            <h4 className="font-bold text-brand-navy flex items-center gap-2" data-id="element-1528">
                              Niño #{idx + 1}
                              <span className="bg-brand-yellow/20 text-amber-700 text-xs px-2 py-0.5 rounded-full" data-id="element-1529">
                                +$40.000
                              </span>
                            </h4>
                            <button onClick={() => removeKid(kid.id)} className="text-red-500 hover:text-red-700 transition-colors p-1" data-id="element-1530">
                              <Trash2 className="w-4 h-4" data-id="element-1531" />
                            </button>
                          </div>
                          <p className="text-xs text-amber-700 bg-amber-100/60 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-id="element-1532">
                            ℹ️ Solo niños menores de 12 años al día del evento
                            (26 de julio de 2026).
                          </p>
                          <p className="text-xs text-brand-navy bg-brand-cyan/10 border border-brand-cyan/30 rounded-lg px-3 py-2 mb-4 flex items-center gap-2" data-id="element-1533">
                            <User className="w-3.5 h-3.5 text-brand-cyan flex-shrink-0" data-id="element-1534" />
                            <span data-id="element-1535">
                              Responsable:{' '}
                              <strong data-id="element-1536">
                                {humans[0]?.name?.trim() || 'Participante Principal (Titular)'}
                              </strong>
                            </span>
                          </p>

                          {/* Kid photo */}
                          <div className="mb-4 pb-4 border-b border-amber-200" data-id="element-1537">
                            <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1538">
                              Foto del niño (opcional)
                            </label>
                            <p className="text-[10px] text-gray-500 mb-3" data-id="element-1539">
                              Nos ayuda a identificarlo el día del evento
                            </p>
                            <div className="w-32" data-id="element-1540">
                              <input type="file" accept="image/*" onChange={e => {
                          const file = e.target.files?.[0] || null;
                          if (!file) return;
                          if (!validatePhotoFile(file)) return;
                          updateKid(kid.id, 'photoFile', file);
                          updateKid(kid.id, 'photoUrlFromMobile', null);
                          toast.success('✓ Foto seleccionada');
                        }} className="hidden" id={`kid-photo-${kid.id}`} data-id="element-1541" />
                              <label htmlFor={`kid-photo-${kid.id}`} className={`relative block w-full aspect-square rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-colors ${kid.photoFile || kid.photoUrlFromMobile ? 'border-brand-green' : 'border-gray-300 hover:border-brand-yellow'}`} data-id="element-1542">
                                {kid.photoFile || kid.photoUrlFromMobile ? <>
                                    <img src={kid.photoFile ? URL.createObjectURL(kid.photoFile) : kid.photoUrlFromMobile!} alt="Foto niño" className="absolute inset-0 w-full h-full object-cover" data-id="element-1543" />
                                    <div className="absolute top-1 right-1 w-6 h-6 bg-brand-green rounded-full flex items-center justify-center shadow" data-id="element-1544">
                                      <Check className="w-4 h-4 text-white" data-id="element-1545" />
                                    </div>
                                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-2" data-id="element-1546">
                                    <Camera className="w-6 h-6 mb-1" data-id="element-1547" />
                                    <span className="text-[10px] text-center font-medium leading-tight" data-id="element-1548">
                                      Subir foto
                                    </span>
                                  </div>}
                              </label>
                              {(kid.photoFile || kid.photoUrlFromMobile) && <button type="button" onClick={() => {
                          updateKid(kid.id, 'photoFile', null);
                          updateKid(kid.id, 'photoUrlFromMobile', null);
                        }} className="mt-2 text-red-500 hover:text-red-700 text-[10px] font-semibold flex items-center justify-center gap-1 w-full" data-id="element-1549">
                                  <Trash2 className="w-3 h-3" data-id="element-1550" /> Quitar foto
                                </button>}
                            </div>
                          </div>

                          {/* Kid name + birthdate */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1551">
                            <div data-id="element-1552">
                              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1553">
                                Nombre completo del niño *
                              </label>
                              <input type="text" value={kid.name} onChange={e => updateKid(kid.id, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-yellow outline-none text-sm" placeholder="Nombre y apellido" data-id="element-1554" />
                            </div>
                            <div data-id="element-1555">
                              <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-1556">
                                Fecha de nacimiento *
                              </label>
                              <input type="date" value={kid.birthdate} max="2026-07-26" onChange={e => updateKid(kid.id, 'birthdate', e.target.value)} className={`w-full px-3 py-2 rounded-lg border outline-none text-sm focus:ring-2 ${ageOk === false ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-brand-yellow'}`} data-id="element-1557" />
                              {ageOk === false && <p className="text-[10px] text-red-600 mt-1" data-id="element-1558">
                                  Solo niños menores de 12 años al 26 de julio
                                  de 2026
                                </p>}
                            </div>
                          </div>
                        </motion.div>;
                })}
                  </AnimatePresence>

                  {/* Add buttons — 2 or 3 depending on ticket type */}
                  <div className={`grid gap-3 ${ticketType === 'pet_lover' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-1559">
                    <button onClick={addHuman} disabled={totalItems >= MAX_TOTAL_ITEMS} className="py-4 border-2 border-dashed border-brand-cyan/50 rounded-2xl text-brand-cyan font-bold flex items-center justify-center gap-2 hover:bg-brand-cyan/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm" data-id="element-1560">
                      <Plus className="w-4 h-4" data-id="element-1561" /> Agregar Adulto (+$70.000)
                    </button>
                    <button onClick={addKid} disabled={totalItems >= MAX_TOTAL_ITEMS} className="py-4 border-2 border-dashed border-brand-yellow rounded-2xl text-amber-700 font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-brand-yellow/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm" data-id="element-1562">
                      <span className="flex items-center gap-2" data-id="element-1563">
                        <Plus className="w-4 h-4" data-id="element-1564" /> Agregar Niño (+$40.000)
                      </span>
                      <span className="text-[10px] font-normal text-amber-600" data-id="element-1565">
                        Solo menores de 12 años
                      </span>
                    </button>
                    {ticketType === 'pet_lover' && <button onClick={() => {
                  if (totalItems >= MAX_TOTAL_ITEMS) {
                    toast.error(`Máximo ${MAX_TOTAL_ITEMS} ítems en total`);
                    return;
                  }
                  setPets([...pets, createLocalPet()]);
                  toast.success('Mascota agregada — completa los datos en el siguiente paso');
                }} disabled={totalItems >= MAX_TOTAL_ITEMS} className="py-4 border-2 border-dashed border-brand-green/50 rounded-2xl text-brand-green font-bold flex items-center justify-center gap-2 hover:bg-brand-green/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm" data-id="element-1566">
                        <Plus className="w-4 h-4" data-id="element-1567" /> Agregar Mascota (+$30.000)
                      </button>}
                  </div>
                  {totalItems >= MAX_TOTAL_ITEMS && <p className="text-center text-amber-600 text-xs mt-2 font-medium" data-id="element-1568">
                      Has alcanzado el máximo de {MAX_TOTAL_ITEMS} ítems
                    </p>}
                </div>

                <div className="flex gap-4 mt-10" data-id="element-1569">
                  <button onClick={() => setStep(1)} className="flex-1 py-4 rounded-xl border-2 border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-1570">
                    <ChevronLeft className="w-5 h-5" data-id="element-1571" /> Atrás
                  </button>
                  <button onClick={() => {
                // Validate adults
                for (let i = 0; i < humans.length; i++) {
                  const h = humans[i];
                  const check = firstError(isValidFullName(h.name), isValidEmail(h.email), isValidColombianPhone(h.phone), isValidCedula(h.idNumber));
                  if (!check.ok) {
                    toast.error(`Participante ${i + 1}: ${check.message}`);
                    return;
                  }
                }
                // Validate kids (photo NOT required)
                for (let i = 0; i < kids.length; i++) {
                  const k = kids[i];
                  if (!isValidFullName(k.name).ok) {
                    toast.error(`Niño ${i + 1}: el nombre es obligatorio`);
                    return;
                  }
                  if (!k.birthdate) {
                    toast.error(`Niño ${i + 1}: la fecha de nacimiento es obligatoria`);
                    return;
                  }
                  if (!isUnder12AtEvent(k.birthdate)) {
                    toast.error(`Niño ${i + 1}: debe ser menor de 12 años al 26 de julio de 2026`);
                    return;
                  }
                }
                setStep(ticketType === 'pet_lover' ? 3 : 4);
              }} className="flex-1 py-4 rounded-xl bg-brand-cyan text-white font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-lg" data-id="element-1572">
                    {ticketType === 'pet_lover' ? 'Continuar → Agregar mascotas' : 'Continuar → Revisar y pagar'}
                    <ChevronRight className="w-5 h-5" data-id="element-1573" />
                  </button>
                </div>
              </motion.div>}

            {/* Step 3: Pet Registration */}
            {step === 3 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1574">
                <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-1575">
                  Registra a {pets.length > 1 ? 'tus mascotas' : 'tu mascota'}
                </h3>
                <p className="text-gray-500 text-center mb-8" data-id="element-1576">
                  {ticketType === 'extra_pet' ? 'Agrega las mascotas adicionales' : 'Tu peludo es el protagonista 🐾'}
                </p>

                <div className="bg-brand-cyan/5 border border-brand-cyan/20 rounded-2xl p-4 flex items-start gap-3 mb-6" data-id="element-1577">
                  <Sparkles className="w-5 h-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-1578" />
                  <p className="text-sm text-gray-700" data-id="element-1579">
                    {ticketType === 'pet_lover' ? <>
                        <strong className="text-brand-navy" data-id="element-1580">
                          Tu mascota principal está incluida.
                        </strong>{' '}
                        Puedes agregar hasta 4 mascotas adicionales por $30.000
                        c/u.
                      </> : <>
                        <strong className="text-brand-navy" data-id="element-1581">
                          $30.000 por mascota adicional.
                        </strong>{' '}
                        Las mascotas se sumarán al Muro de las Huellas 🐾
                      </>}
                  </p>
                </div>

                <div className="space-y-6" data-id="element-1582">
                  <AnimatePresence data-id="element-1583">
                    {pets.map((pet, index) => {
                  const isPrincipal = index === 0 && ticketType === 'pet_lover';
                  const title = isPrincipal ? 'Mascota Principal' : `Mascota Extra #${ticketType === 'extra_pet' ? index + 1 : index}`;
                  const badge = !isPrincipal ? <span className="bg-brand-yellow/20 text-brand-yellow text-xs px-2 py-0.5 rounded-full ml-2" data-id="element-1584">
                          {ticketType === 'extra_pet' ? '$30.000' : '+$30.000'}
                        </span> : null;
                  return <motion.div key={pet.id} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="space-y-3" data-id="element-1585">
                          <PetFormCard pet={pet} index={index} canRemove={pets.length > 1} title={title} badge={badge} onUpdate={fn => updatePet(pet.id, p => fn(p) as LocalPetEntry)} onRemove={() => removePet(pet.id)} onPhotoChange={e => handlePetPhotoChange(pet.id, e)} data-id="element-1586" />

                          {/* Responsables Block */}
                          {ticketType === 'pet_lover' && <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm ml-4 border-l-4 border-l-brand-cyan" data-id="element-1587">
                              <p className="text-sm font-bold text-brand-navy mb-3" data-id="element-1588">
                                ¿Quién es responsable de esta mascota? *
                              </p>
                              <div className="space-y-2" data-id="element-1589">
                                {humans.map(human => <label key={human.id} className="flex items-center gap-3 cursor-pointer" data-id="element-1590">
                                    <input type="checkbox" checked={pet.responsables.includes(human.id)} onChange={e => {
                            updatePet(pet.id, p => ({
                              ...p,
                              responsables: e.target.checked ? [...p.responsables, human.id] : p.responsables.filter(id => id !== human.id)
                            }));
                          }} className="w-4 h-4 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan cursor-pointer" data-id="element-1591" />
                                    <span className="text-sm text-gray-700" data-id="element-1592">
                                      {human.name || 'Participante sin nombre'}
                                    </span>
                                  </label>)}
                              </div>
                            </div>}
                        </motion.div>;
                })}
                  </AnimatePresence>

                  {pets.length < 5 && <button onClick={addPet} className="w-full py-4 border-2 border-dashed border-brand-cyan/50 rounded-2xl text-brand-cyan font-bold flex items-center justify-center gap-2 hover:bg-brand-cyan/5 transition-colors" data-id="element-1593">
                      <Plus className="w-5 h-5" data-id="element-1594" /> Agregar otra mascota
                      (+$30.000)
                    </button>}
                </div>

                {/* Pet-specific legal acceptance */}
                <div className="mt-8 bg-amber-50/50 border border-amber-200 rounded-2xl p-6 space-y-3" data-id="element-1595">
                  <h4 className="font-bold text-brand-navy mb-2 text-sm uppercase tracking-wider" data-id="element-1596">
                    Aceptación para mascotas
                  </h4>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1597">
                    <input type="checkbox" checked={acceptedPetTerms} onChange={e => setAcceptedPetTerms(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-1598" />
                    <span className="text-sm text-gray-700 leading-relaxed" data-id="element-1599">
                      Acepto los{' '}
                      <a href="/terminos#mascotas" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-1600">
                        Términos y Condiciones (sección Mascotas)
                      </a>{' '}
                      del evento Latido & Huella.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-1601">
                    <input type="checkbox" checked={acceptedPetHabeas} onChange={e => setAcceptedPetHabeas(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-1602" />
                    <span className="text-sm text-gray-700 leading-relaxed" data-id="element-1603">
                      Autorizo el{' '}
                      <a href="/terminos#datos" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-1604">
                        tratamiento de mis datos personales (Habeas Data)
                      </a>
                      .
                    </span>
                  </label>
                </div>

                <div className="flex gap-4 mt-10" data-id="element-1605">
                  <button onClick={() => setStep(ticketType === 'extra_pet' ? 1.5 : 2)} className="flex-1 py-4 rounded-xl border-2 border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-1606">
                    <ChevronLeft className="w-5 h-5" data-id="element-1607" /> Atrás
                  </button>
                  <button onClick={() => {
                if (ticketType === 'pet_lover') {
                  for (let i = 0; i < pets.length; i++) {
                    if (pets[i].responsables.length === 0) {
                      toast.error(`Mascota ${i + 1}: Selecciona al menos un responsable`);
                      return;
                    }
                  }
                }
                setStep(4);
              }} disabled={!acceptedPetTerms || !acceptedPetHabeas} className="flex-1 py-4 rounded-xl bg-brand-cyan text-white font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1608">
                    Continuar → Revisar y pagar
                    <ChevronRight className="w-5 h-5" data-id="element-1609" />
                  </button>
                </div>
                {(!acceptedPetTerms || !acceptedPetHabeas) && <p className="text-center text-amber-600 text-xs mt-3 font-medium" data-id="element-1610">
                    Debes aceptar los Términos de mascotas y el Habeas Data para
                    continuar
                  </p>}
              </motion.div>}

            {/* Step 4: Summary */}
            {step === 4 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1611">
                <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-1612">
                  Resumen de tu inscripción
                </h3>
                <p className="text-gray-500 text-center mb-8" data-id="element-1613">
                  Verifica tus datos antes de continuar al pago
                </p>

                <div className="space-y-6" data-id="element-1614">
                  {/* Pack info */}
                  <div className="bg-brand-cyan/5 border border-brand-cyan/20 rounded-2xl p-6" data-id="element-1615">
                    <div className="flex justify-between items-center mb-4" data-id="element-1616">
                      <div data-id="element-1617">
                        <h4 className="font-bold text-brand-navy text-lg" data-id="element-1618">
                          {ticketType === 'pet_lover' && 'Pack Pet Lover'}
                          {ticketType === 'deportista' && 'Pack Deportista'}
                          {ticketType === 'extra_pet' && 'Mascotas Extra'}
                        </h4>
                        <p className="text-gray-500 text-sm" data-id="element-1619">
                          {ticketType === 'pet_lover' && '1 persona + 1 mascota'}
                          {ticketType === 'deportista' && '1 persona sin mascota'}
                          {ticketType === 'extra_pet' && 'Adicional a inscripción existente'}
                        </p>
                      </div>
                    </div>

                    {/* Breakdown — discriminated by item type */}
                    <div className="border-t border-brand-cyan/10 pt-3 space-y-1.5 text-sm" data-id="element-1620">
                      {ticketType !== 'extra_pet' && humans.length > 0 && <div className="flex justify-between text-gray-600" data-id="element-1621">
                          <span data-id="element-1622">
                            {humans.length} Adulto{humans.length > 1 ? 's' : ''}{' '}
                            <span className="text-gray-400 text-xs" data-id="element-1623">
                              ($70.000 c/u)
                            </span>
                          </span>
                          <span className="font-medium" data-id="element-1624">
                            {formatCOP(humans.length * 70000)}
                          </span>
                        </div>}
                      {ticketType !== 'extra_pet' && kids.length > 0 && <div className="flex justify-between text-gray-600" data-id="element-1625">
                          <span data-id="element-1626">
                            {kids.length} Niño{kids.length > 1 ? 's' : ''}{' '}
                            <span className="text-gray-400 text-xs" data-id="element-1627">
                              ($40.000 c/u)
                            </span>
                          </span>
                          <span className="font-medium" data-id="element-1628">
                            {formatCOP(kids.length * 40000)}
                          </span>
                        </div>}
                      {(ticketType === 'pet_lover' || ticketType === 'extra_pet') && pets.length > 0 && <div className="flex justify-between text-gray-600" data-id="element-1629">
                            <span data-id="element-1630">
                              {pets.length} Mascota{pets.length > 1 ? 's' : ''}{' '}
                              <span className="text-gray-400 text-xs" data-id="element-1631">
                                ($30.000 c/u)
                              </span>
                            </span>
                            <span className="font-medium" data-id="element-1632">
                              {formatCOP(pets.length * 30000)}
                            </span>
                          </div>}

                      <div className="flex justify-between text-gray-600 pt-2 border-t border-brand-cyan/10 mt-2" data-id="element-1633">
                        <span data-id="element-1634">Subtotal</span>
                        <span className="font-medium" data-id="element-1635">{formatCOP(price)}</span>
                      </div>
                      {appliedDiscount && <div className="flex justify-between text-green-600 font-semibold" data-id="element-1636">
                          <span data-id="element-1637">Descuento ({appliedDiscount.code})</span>
                          <span data-id="element-1638">-{formatCOP(discountAmount)}</span>
                        </div>}
                      <div className="flex justify-between text-gray-600" data-id="element-1639">
                        <span data-id="element-1640">IVA (19%)</span>
                        <span className="font-medium" data-id="element-1641">
                          {formatCOP(calculatedIva)}
                        </span>
                      </div>
                      <div className="flex justify-between text-brand-navy pt-2 border-t border-brand-cyan/20 mt-1" data-id="element-1642">
                        <span className="font-bold" data-id="element-1643">Total a pagar</span>
                        <span className="font-black text-2xl" data-id="element-1644">
                          {formatCOP(finalTotal)}
                        </span>
                      </div>
                    </div>

                    {/* Discount code input */}
                    <div className="mt-4 pt-4 border-t border-brand-cyan/10" data-id="element-1645">
                      <DiscountCodeInput onApply={setAppliedDiscount} disabled={isSubmitting} data-id="element-1646" />
                    </div>
                  </div>

                  {/* Human summary */}
                  <div className="bg-gray-50 rounded-2xl p-6" data-id="element-1647">
                    <h4 className="font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-1648">
                      <User className="w-5 h-5 text-brand-cyan" data-id="element-1649" /> Participantes
                      ({humans.length})
                      {ticketType === 'extra_pet' && <span className="text-xs font-normal text-gray-500" data-id="element-1650">
                          (Validado)
                        </span>}
                    </h4>
                    <div className="space-y-4" data-id="element-1651">
                      {humans.map((human, idx) => <div key={human.id} className={idx > 0 ? 'pt-4 border-t border-gray-200' : ''} data-id="element-1652">
                          <p className="font-semibold text-brand-navy text-sm mb-2" data-id="element-1653">
                            {idx === 0 ? 'Principal: ' : `Acompañante #${idx}: `}
                            {human.name}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-id="element-1654">
                            <div className="min-w-0" data-id="element-1655">
                              <span className="text-gray-500 block text-xs" data-id="element-1656">
                                Documento
                              </span>
                              <strong className="block break-words" data-id="element-1657">
                                {human.idNumber}
                              </strong>
                            </div>
                            <div className="min-w-0" data-id="element-1658">
                              <span className="text-gray-500 block text-xs" data-id="element-1659">
                                Email
                              </span>
                              <strong className="block break-all" data-id="element-1660">
                                {human.email}
                              </strong>
                            </div>
                            <div className="min-w-0" data-id="element-1661">
                              <span className="text-gray-500 block text-xs" data-id="element-1662">
                                WhatsApp
                              </span>
                              <strong className="block break-words" data-id="element-1663">
                                {human.phone}
                              </strong>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Kids summary */}
                  {ticketType !== 'extra_pet' && kids.length > 0 && <div className="bg-amber-50/40 rounded-2xl p-6 border border-amber-200" data-id="element-1664">
                      <h4 className="font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-1665">
                        <User className="w-5 h-5 text-amber-600" data-id="element-1666" /> Niños (
                        {kids.length})
                      </h4>
                      <div className="space-y-4" data-id="element-1667">
                        {kids.map((kid, idx) => <div key={kid.id} className={idx > 0 ? 'pt-4 border-t border-amber-200' : ''} data-id="element-1668">
                            <div className="flex items-center gap-3 mb-2" data-id="element-1669">
                              {(kid.photoFile || kid.photoUrlFromMobile) && <img src={kid.photoFile ? URL.createObjectURL(kid.photoFile) : kid.photoUrlFromMobile!} alt={kid.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-1670" />}
                              <p className="font-semibold text-brand-navy text-sm" data-id="element-1671">
                                Niño #{idx + 1}: {kid.name}
                              </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-id="element-1672">
                              <div className="min-w-0" data-id="element-1673">
                                <span className="text-gray-500 block text-xs" data-id="element-1674">
                                  Fecha de nacimiento
                                </span>
                                <strong className="block" data-id="element-1675">
                                  {kid.birthdate}
                                </strong>
                              </div>
                              <div className="min-w-0" data-id="element-1676">
                                <span className="text-gray-500 block text-xs" data-id="element-1677">
                                  Precio
                                </span>
                                <strong className="block" data-id="element-1678">
                                  {formatCOP(40000)}
                                </strong>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                  {/* Pets summary */}
                  {(ticketType === 'pet_lover' || ticketType === 'extra_pet') && pets.length > 0 && pets[0].name && <div className="bg-gray-50 rounded-2xl p-6" data-id="element-1679">
                        <h4 className="font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-1680">
                          <PawPrint className="w-5 h-5 text-brand-cyan" data-id="element-1681" />{' '}
                          Mascotas ({pets.length})
                        </h4>
                        <div className="space-y-4" data-id="element-1682">
                          {pets.map((pet, idx) => <div key={pet.id} className={idx > 0 ? 'pt-4 border-t border-gray-200' : ''} data-id="element-1683">
                              <div className="flex items-center gap-3 mb-2" data-id="element-1684">
                                {(pet.photoFile || pet.photoUrlFromMobile) && <img src={pet.photoFile ? URL.createObjectURL(pet.photoFile) : pet.photoUrlFromMobile!} alt={pet.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-1685" />}
                                <p className="font-semibold text-brand-navy text-sm" data-id="element-1686">
                                  {idx === 0 && ticketType === 'pet_lover' ? 'Principal: ' : 'Extra: '}
                                  {pet.name}
                                </p>
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-sm mb-2" data-id="element-1687">
                                <div data-id="element-1688">
                                  <span className="text-gray-500 block text-xs" data-id="element-1689">
                                    Raza
                                  </span>
                                  <strong data-id="element-1690">{pet.breed}</strong>
                                </div>
                                <div data-id="element-1691">
                                  <span className="text-gray-500 block text-xs" data-id="element-1692">
                                    Edad
                                  </span>
                                  <strong data-id="element-1693">{pet.age || '—'}</strong>
                                </div>
                                <div data-id="element-1694">
                                  <span className="text-gray-500 block text-xs" data-id="element-1695">
                                    Tamaño
                                  </span>
                                  <strong data-id="element-1696">{pet.size}</strong>
                                </div>
                              </div>
                              {ticketType === 'pet_lover' && pet.responsables.length > 0 && <div className="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200" data-id="element-1697">
                                    <span className="font-semibold" data-id="element-1698">
                                      Responsables:
                                    </span>{' '}
                                    {pet.responsables.map(rId => humans.find(h => h.id === rId)?.name).filter(Boolean).join(', ')}
                                  </div>}
                            </div>)}
                        </div>
                      </div>}

                  {/* Includes */}
                  {ticketType !== 'extra_pet' && <div className="bg-gray-50 rounded-2xl p-6" data-id="element-1699">
                      <h4 className="font-bold text-brand-navy mb-3" data-id="element-1700">
                        Tu inscripción incluye:
                      </h4>
                      <ul className="space-y-2" data-id="element-1701">
                        {['Kit de bienvenida', 'Perfil digital con QR único', 'Certificado digital al finalizar', 'Acceso completo a la feria', ...(ticketType === 'pet_lover' ? ['Participación en concurso Mascota Influencer'] : [])].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1702">
                            <Check className="w-4 h-4 text-brand-green" data-id="element-1703" />{' '}
                            {item}
                          </li>)}
                      </ul>
                    </div>}

                  {/* Legal Checkboxes */}
                  <div className="mt-8 bg-amber-50/50 border border-amber-200 rounded-2xl p-6 space-y-3" data-id="element-1704">
                    <h4 className="font-bold text-brand-navy mb-2 text-sm uppercase tracking-wider" data-id="element-1705">
                      Aceptación legal
                    </h4>
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-1706">
                      <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-1707" />
                      <span className="text-sm text-gray-700 leading-relaxed" data-id="element-1708">
                        Acepto los{' '}
                        <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-1709">
                          Términos y Condiciones
                        </a>{' '}
                        del evento Latido & Huella.
                      </span>
                    </label>
                    {ticketType !== 'deportista' && <label className="flex items-start gap-3 cursor-pointer" data-id="element-1710">
                        <input type="checkbox" checked={acceptedReglamento} onChange={e => setAcceptedReglamento(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-1711" />
                        <span className="text-sm text-gray-700 leading-relaxed" data-id="element-1712">
                          Acepto el{' '}
                          <a href="/terminos#caminata" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-1713">
                            Reglamento y Consentimiento Informado
                          </a>{' '}
                          de la caminata canina (responsabilidad sobre mi
                          mascota, vacunación al día y comportamiento durante el
                          recorrido).
                        </span>
                      </label>}
                  </div>

                  <div className="flex gap-4 mt-10" data-id="element-1714">
                    <button onClick={() => setStep(ticketType === 'deportista' ? 2 : 3)} className="flex-1 py-4 rounded-xl border-2 border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-1715">
                      <ChevronLeft className="w-5 h-5" data-id="element-1716" /> Editar
                    </button>
                    <button onClick={handleRegistration} disabled={isSubmitting || !acceptedTerms || ticketType !== 'deportista' && !acceptedReglamento} className="flex-1 py-4 rounded-xl bg-brand-cyan text-white font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors shadow-xl text-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1717">
                      {isSubmitting ? 'Registrando...' : `Ir al pago · ${formatCOP(priceBreakdown(price).total)}`}
                    </button>
                  </div>
                  {(!acceptedTerms || ticketType !== 'deportista' && !acceptedReglamento) && <p className="text-center text-amber-600 text-xs mt-3 font-medium" data-id="element-1718">
                      {ticketType === 'deportista' ? 'Debes aceptar los Términos para continuar al pago' : 'Debes aceptar los Términos y el Reglamento para continuar al pago'}
                    </p>}
                  <p className="text-center text-gray-400 text-xs mt-4" data-id="element-1719">
                    Tu inscripción se registra en nuestro sistema y serás
                    redirigido al pago seguro con Wompi.
                  </p>
                </div>
              </motion.div>}
          </div>

          {/* Social cause banner */}
          <div className="mt-12 bg-brand-green/10 border border-brand-green/20 rounded-2xl p-6 text-center" data-id="element-1720">
            <p className="text-brand-green font-bold flex items-center justify-center gap-2" data-id="element-1721">
              <Heart className="w-5 h-5 fill-brand-green" data-id="element-1722" />
              Parte de tu inscripción apoya al CEIBA Rionegro, nuestra fundación
              aliada 2026.
            </p>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-16 bg-white" data-id="element-1723">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1724">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="mb-16 bg-brand-navy rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden" data-id="element-1725">
            <div className="absolute inset-0 bg-paw-pattern-white opacity-5 pointer-events-none" data-id="element-1726"></div>
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-brand-cyan/10 rounded-full blur-3xl pointer-events-none" data-id="element-1727"></div>
            <div className="relative z-10" data-id="element-1728">
              <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-2" data-id="element-1729">
                Durante el recorrido y con tu inscripción contarás con:
              </h3>
              <div className="w-16 h-1 bg-brand-cyan mx-auto rounded-full mb-10" data-id="element-1730"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-1731">
                {[{
                icon: Gift,
                title: 'Kit de Participante',
                description: 'Obsequios especiales para humanos y mascotas.',
                color: 'bg-brand-yellow',
                textColor: 'text-brand-yellow'
              }, {
                icon: Droplets,
                title: 'Zonas de Hidratación',
                description: 'Puntos estratégicos para refrescarte a ti y a tus peludos.',
                color: 'bg-brand-cyan',
                textColor: 'text-brand-cyan'
              }, {
                icon: ShieldCheck,
                title: 'Seguridad y Cuidado',
                description: 'Acompañamiento médico y veterinario durante todo el trayecto.',
                color: 'bg-brand-green',
                textColor: 'text-brand-green'
              }, {
                icon: Award,
                title: 'Certificado de Participación',
                description: 'Reconocimiento digital por ser parte del movimiento.',
                color: 'bg-white',
                textColor: 'text-white'
              }].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.45,
                delay: 0.2 + index * 0.08
              }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 flex items-center gap-4 hover:bg-white/10 transition-all duration-300 group" data-id="element-1732">
                    <div className={`w-12 h-12 ${item.color} bg-opacity-20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`} data-id="element-1733">
                      <item.icon className={`w-6 h-6 ${item.textColor}`} data-id="element-1734" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1735">
                      <h4 className="text-white font-bold text-base leading-tight mb-0.5" data-id="element-1736">
                        {item.title}
                      </h4>
                      <p className="text-white/70 text-xs md:text-sm leading-snug" data-id="element-1737">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
              <p className="text-white/50 text-xs text-center mt-6 italic max-w-2xl mx-auto leading-relaxed" data-id="element-1738">
                * La personalización del kit con el nombre de tu mascota solo
                está garantizada para inscripciones realizadas antes del 20 de
                junio.
              </p>
            </div>
          </motion.div>

          {/* Route Info */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12 mb-16" data-id="element-1739">
            <h2 className="text-3xl font-bold text-brand-navy mb-6 text-center" data-id="element-1740">
              {['El', 'Recorrido'].map((word, i) => <motion.span key={i} initial={{
              opacity: 0,
              y: 30,
              filter: 'blur(10px)'
            }} whileInView={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: i * 0.15,
              ease: [0.22, 1, 0.36, 1]
            }} className="inline-block shimmer-text mr-3" data-id="element-1741">
                  {word}
                </motion.span>)}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center" data-id="element-1742">
              {['Parque del Bienestar COMFAMA', 'Vereda 3 Puertas', 'Vía Linares', 'Vereda Villachiquaga', 'Meta Parque del Bienestar COMFAMA.'].map((point, i) => <div key={i} className="flex flex-col items-center" data-id="element-1743">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mb-2 ${i === 0 ? 'bg-brand-green' : i === 4 ? 'bg-brand-yellow' : 'bg-brand-cyan'}`} data-id="element-1744">
                    {i + 1}
                  </div>
                  <span className="text-sm font-medium text-gray-700" data-id="element-1745">
                    {point}
                  </span>
                  {i < 4 && <ChevronRight className="w-5 h-5 text-gray-300 mt-2 hidden md:block" data-id="element-1746" />}
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}